import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ContainerComponent,
  FormCheckComponent,
  FormCheckInputDirective,
  FormCheckLabelDirective,
  FormControlDirective
} from '@coreui/angular-pro';
import { TranslatePipe, TranslateService } from 'i18n';
import { ExplorerMedia, MediaExplorerComponent, MediaExplorerSource, MediaFileEntry } from 'media';
import { Subscription } from 'rxjs';
import { Logger } from 'shared';
import {
  AdminSalesToolkitService,
  SALES_TOOLKIT_EXTENSIONS,
  SalesToolkitErrorCode,
  maxBytesFor,
  maxMegabytesFor
} from './admin-sales-toolkit.service';

export type SalesToolkitUploadState =
  | 'queued'
  | 'uploading'
  | 'ok'
  | 'replaced'
  | 'skipped_exists'
  | 'rejected_type'
  | 'rejected_size'
  | 'failed';

export interface SalesToolkitUpload {
  readonly id: number;
  readonly file: File;
  /** What the queue shows: the path inside the drop/folder, or the file name. */
  readonly label: string;
  /** Folder the upload goes to, fixed when it was queued (the admin may browse on). */
  readonly targetPath: string;
  readonly relativePath?: string;
  readonly overwrite: boolean;
  progress: number;
  state: SalesToolkitUploadState;
  /** Translation key of a reason, for `failed`. */
  reason?: string;
}

/** A file picked up from a drop or a picker, before it is queued. */
interface PickedFile {
  readonly file: File;
  readonly relativePath?: string;
}

/** Uploads in flight at once - enough to keep the line busy, few enough not to starve the listing. */
const MAX_CONCURRENT_UPLOADS = 3;

const ALLOWED = new Set<string>(SALES_TOOLKIT_EXTENSIONS);

function extensionOf(file: File): string {
  return (file.name.split('.').pop() ?? '').toLowerCase();
}

/**
 * Admin file manager for the brand's Sales Toolkit - the folder members see as
 * the first entry under Information.
 *
 * Deliberately the members' explorer with admin controls on top, not a second
 * file list: what the admin arranges here is exactly what members will see.
 * The backend binds everything to the brand of the host, so this page never
 * names a brand.
 *
 * Uploading is the bulk of the work, so it is built for whole folders: drag a
 * folder from the desktop (or use "Upload folder") and its structure is
 * recreated, files that already exist are skipped unless "Replace existing" is
 * on, and dotfiles and file types the toolkit does not take are dropped before
 * anything is sent.
 */
@Component({
  selector: 'app-admin-sales-toolkit-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ContainerComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ButtonDirective,
    FormControlDirective,
    FormCheckComponent,
    FormCheckInputDirective,
    FormCheckLabelDirective,
    MediaExplorerComponent,
    TranslatePipe
  ],
  templateUrl: './admin-sales-toolkit-page.component.html',
  styleUrls: ['./admin-sales-toolkit-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminSalesToolkitPageComponent {
  private logger = new Logger('AdminSalesToolkitPageComponent');
  private service = inject(AdminSalesToolkitService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  /** The toolkit as an explorer item. Its title is the same in every language (customer request). */
  readonly media: ExplorerMedia = {
    id: 'sales_toolkit',
    type: 'explorer',
    name: 'Sales Toolkit',
    cover: { src: '', mimeType: 'image/jpeg', width: 0, height: 0 },
    properties: { explorer: { area: 'sales_toolkit', path: '' } }
  };

  readonly source: MediaExplorerSource = path => this.service.browse(path);

  readonly accept = SALES_TOOLKIT_EXTENSIONS.map(extension => `.${extension}`).join(',');

  private explorer = viewChild(MediaExplorerComponent);

  /** Folder the explorer shows - where new folders and uploads go. */
  readonly currentPath = computed(() => this.explorer()?.currentPath() ?? '');

  error = signal<string | null>(null);
  busy = signal(false);

  creatingFolder = signal(false);
  newFolderName = signal('');

  /** Path of the entry being renamed inline, and the name typed so far. */
  renaming = signal<string | null>(null);
  renameValue = signal('');

  overwrite = signal(false);
  dragActive = signal(false);
  /** dragenter/dragleave fire for every child element crossed; only the count says when the drag left. */
  private dragDepth = 0;

  uploads = signal<SalesToolkitUpload[]>([]);
  /** Dotfiles and disallowed types dropped before queueing, for the summary line. */
  skipped = signal(0);
  readonly uploading = computed(() => this.uploads().some(upload => upload.state === 'queued' || upload.state === 'uploading'));
  readonly doneCount = computed(() => this.uploads().filter(upload => upload.state !== 'queued' && upload.state !== 'uploading').length);

  private nextUploadId = 1;
  private active = new Map<number, Subscription>();
  /** Whether the running batch changed the folder, i.e. whether the listing must be re-read. */
  private batchChanged = false;

  constructor() {
    this.destroyRef.onDestroy(() => this.active.forEach(subscription => subscription.unsubscribe()));
  }

  // --- Folders, rename, delete -----------------------------------------------

  startNewFolder(): void {
    this.error.set(null);
    this.newFolderName.set('');
    this.creatingFolder.set(true);
  }

  cancelNewFolder(): void {
    this.creatingFolder.set(false);
  }

  createFolder(): void {
    const name = this.newFolderName().trim();
    if (!name) {
      this.error.set(this.translate.translate('sales_toolkit.errors.name_invalid'));
      return;
    }
    this.busy.set(true);
    this.service.createDirectory(this.currentPath(), name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.creatingFolder.set(false);
          this.error.set(null);
          this.refresh();
        },
        error: err => this.fail('Failed to create folder', err)
      });
  }

  startRename(entry: MediaFileEntry): void {
    this.error.set(null);
    this.renaming.set(entry.path);
    this.renameValue.set(entry.name);
  }

  cancelRename(): void {
    this.renaming.set(null);
  }

  saveRename(entry: MediaFileEntry): void {
    const name = this.renameValue().trim();
    if (!name) {
      this.error.set(this.translate.translate('sales_toolkit.errors.name_invalid'));
      return;
    }
    if (name === entry.name) {
      this.renaming.set(null);
      return;
    }
    this.busy.set(true);
    this.service.rename(entry.path, name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.renaming.set(null);
          this.error.set(null);
          this.refresh();
        },
        error: err => this.fail('Failed to rename entry', err)
      });
  }

  remove(entry: MediaFileEntry): void {
    const key = entry.kind === 'directory'
      ? 'sales_toolkit.prompts.confirm_delete_directory'
      : 'sales_toolkit.prompts.confirm_delete_file';
    if (!confirm(this.translate.translate(key, { name: entry.name }))) {
      return;
    }
    this.busy.set(true);
    this.service.delete(entry.path)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.error.set(null);
          this.refresh();
        },
        error: err => this.fail('Failed to delete entry', err)
      });
  }

  refresh(): void {
    this.explorer()?.reload();
  }

  private fail(message: string, err: unknown): void {
    this.logger.error(message, err);
    this.busy.set(false);
    this.error.set(this.translate.translate(this.errorKey(err)));
  }

  private errorKey(err: unknown): string {
    switch (this.errorCode(err)) {
      case 'exists': return 'sales_toolkit.errors.exists';
      case 'name_invalid': return 'sales_toolkit.errors.name_invalid';
      case 'not_found': return 'sales_toolkit.errors.not_found';
      default: return 'sales_toolkit.errors.action_failed';
    }
  }

  private errorCode(err: unknown): SalesToolkitErrorCode | null {
    const body = err instanceof HttpErrorResponse ? err.error : null;
    return (body && typeof body === 'object' && typeof body.error === 'string') ? body.error : null;
  }

  // --- Upload sources --------------------------------------------------------

  onFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const picked = Array.from(input.files ?? []).map(file => ({ file }));
    input.value = ''; // picking the same files again must fire change again
    this.enqueue(picked);
  }

  /** Folder picker (`webkitdirectory`): every file carries its path from the picked folder. */
  onFolderPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const picked = Array.from(input.files ?? []).map(file => ({
      file,
      relativePath: file.webkitRelativePath || undefined
    }));
    input.value = '';
    this.enqueue(picked);
  }

  onDragEnter(event: DragEvent): void {
    if (!this.carriesFiles(event)) {
      return;
    }
    event.preventDefault();
    this.dragDepth++;
    this.dragActive.set(true);
  }

  onDragOver(event: DragEvent): void {
    if (!this.carriesFiles(event)) {
      return;
    }
    // Without preventDefault on dragover the browser refuses the drop and opens
    // the file itself instead.
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onDragLeave(event: DragEvent): void {
    if (!this.carriesFiles(event)) {
      return;
    }
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.dragActive.set(false);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragDepth = 0;
    this.dragActive.set(false);
    const transfer = event.dataTransfer;
    if (!transfer) {
      return;
    }
    // The DataTransfer is only readable during the event: entries and files
    // must be taken out synchronously, the directory walk may run afterwards.
    const items = Array.from(transfer.items ?? []).filter(item => item.kind === 'file');
    const entries = items.map(item => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null));
    const flat = Array.from(transfer.files ?? []).map(file => ({ file }));
    const canWalk = entries.length > 0 && entries.every(entry => !!entry);

    if (!canWalk) {
      // No directory API (or nothing but plain files from an odd source): the flat
      // file list loses folders, but single files still arrive.
      this.enqueue(flat);
      return;
    }
    this.collect(entries as FileSystemEntry[])
      .then(picked => this.enqueue(picked))
      .catch(error => {
        this.logger.error('Failed to read dropped folder', error);
        this.error.set(this.translate.translate('sales_toolkit.errors.read_failed'));
      });
  }

  private carriesFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
  }

  /**
   * Walks dropped entries. A top-level file goes in as-is; everything inside a
   * dropped folder keeps its path from that folder (its `fullPath`, which starts
   * at the dropped root), so the backend recreates the structure. Hidden entries
   * are not even descended into - a `.git` or `__MACOSX/._x` has no place here.
   */
  private async collect(entries: FileSystemEntry[]): Promise<PickedFile[]> {
    const result: PickedFile[] = [];
    const walk = async (entry: FileSystemEntry, topLevel: boolean): Promise<void> => {
      if (entry.name.startsWith('.')) {
        this.skipped.update(count => count + 1);
        return;
      }
      if (entry.isFile) {
        const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
        result.push({ file, relativePath: topLevel ? undefined : entry.fullPath.replace(/^\/+/, '') });
        return;
      }
      if (entry.isDirectory) {
        const children = await this.readAll((entry as FileSystemDirectoryEntry).createReader());
        for (const child of children) {
          await walk(child, false);
        }
      }
    };
    for (const entry of entries) {
      await walk(entry, true);
    }
    return result;
  }

  /** readEntries answers in batches (Chrome: 100 at most) - only an empty batch means "done". */
  private async readAll(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
    const all: FileSystemEntry[] = [];
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
      if (!batch.length) {
        return all;
      }
      all.push(...batch);
    }
  }

  // --- Upload queue ----------------------------------------------------------

  /**
   * Filters and queues picked files. Dotfiles and types the backend refuses are
   * counted, not sent - they would only come back as errors. Oversized files are
   * queued as rejected so the admin sees which ones did not make it.
   */
  private enqueue(picked: PickedFile[]): void {
    const targetPath = this.currentPath();
    const overwrite = this.overwrite();
    const queued: SalesToolkitUpload[] = [];
    let skipped = 0;

    for (const { file, relativePath } of picked) {
      const segments = (relativePath || file.name).split('/').filter(segment => !!segment);
      if (!segments.length || segments.some(segment => segment.startsWith('.'))) {
        skipped++;
        continue;
      }
      const extension = extensionOf(file);
      if (!file.name.includes('.') || !ALLOWED.has(extension)) {
        skipped++;
        continue;
      }
      const tooLarge = file.size > maxBytesFor(extension);
      queued.push({
        id: this.nextUploadId++,
        file,
        label: relativePath || file.name,
        targetPath,
        relativePath,
        overwrite,
        progress: 0,
        state: tooLarge ? 'rejected_size' : 'queued'
      });
    }

    if (skipped) {
      this.skipped.update(count => count + skipped);
    }
    if (!queued.length) {
      return;
    }
    this.uploads.update(list => [...list, ...queued]);
    this.pump();
  }

  /** Starts queued uploads until MAX_CONCURRENT_UPLOADS are in flight; re-reads the folder when all are through. */
  private pump(): void {
    for (const upload of this.uploads()) {
      if (this.active.size >= MAX_CONCURRENT_UPLOADS) {
        break;
      }
      if (upload.state === 'queued' && !this.active.has(upload.id)) {
        this.start(upload);
      }
    }
    if (!this.active.size && !this.uploads().some(upload => upload.state === 'queued')) {
      if (this.batchChanged) {
        this.batchChanged = false;
        this.refresh();
      }
    }
  }

  private start(upload: SalesToolkitUpload): void {
    this.patchUpload(upload.id, { state: 'uploading', progress: 0 });
    const subscription = this.service
      .uploadFile(upload.targetPath, upload.file, { relativePath: upload.relativePath, overwrite: upload.overwrite })
      .subscribe({
        next: event => {
          if (event.type === HttpEventType.UploadProgress) {
            const total = event.total || upload.file.size || 1;
            this.patchUpload(upload.id, { progress: Math.min(100, Math.round(100 * event.loaded / total)) });
          } else if (event.type === HttpEventType.Response) {
            const body = event.body as { replaced?: boolean; entry?: { replaced?: boolean } } | null;
            const replaced = body?.replaced === true || body?.entry?.replaced === true;
            this.batchChanged = true;
            this.finish(upload.id, { state: replaced ? 'replaced' : 'ok', progress: 100 });
          }
        },
        error: (err: unknown) => {
          this.logger.warn('Upload failed', { label: upload.label, err });
          this.finish(upload.id, this.uploadFailure(err));
        }
      });
    this.active.set(upload.id, subscription);
  }

  private uploadFailure(err: unknown): Partial<SalesToolkitUpload> {
    const code = this.errorCode(err);
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    if (code === 'exists') {
      // A file of that name is there and "replace" was off; with a folder in the
      // way it is the same answer - nothing was changed.
      return { state: 'skipped_exists' };
    }
    if (code === 'type_not_allowed') {
      return { state: 'rejected_type' };
    }
    // 413 is what a proxy in front of Rails answers before the app sees the file.
    if (code === 'too_large' || status === 413) {
      return { state: 'rejected_size' };
    }
    if (code === 'name_invalid') {
      return { state: 'failed', reason: 'sales_toolkit.errors.name_invalid' };
    }
    return { state: 'failed' };
  }

  private finish(id: number, patch: Partial<SalesToolkitUpload>): void {
    this.active.get(id)?.unsubscribe();
    this.active.delete(id);
    this.patchUpload(id, patch);
    this.pump();
  }

  private patchUpload(id: number, patch: Partial<SalesToolkitUpload>): void {
    this.uploads.update(list => list.map(upload => (upload.id === id ? { ...upload, ...patch } : upload)));
  }

  /** Closes the queue panel. Results stay until then, so failures are not missed. */
  clearUploads(): void {
    if (this.uploading()) {
      return;
    }
    this.uploads.set([]);
    this.skipped.set(0);
  }

  /**
   * Parameters of the state text. Only `rejected_size` uses one: the limit that
   * applied to THIS file (20 or 500 MB), also when the refusal came back as a
   * 413 from the server rather than from the check before uploading. A number
   * only - the unit stays in the translation, where it is "Mo" or "МБ".
   */
  stateParams(upload: SalesToolkitUpload): Record<string, string> {
    return upload.state === 'rejected_size' ? { limit: String(maxMegabytesFor(extensionOf(upload.file))) } : {};
  }

  stateClass(state: SalesToolkitUploadState): string {
    switch (state) {
      case 'ok':
      case 'replaced':
        return 'text-success';
      case 'skipped_exists':
        return 'text-warning';
      case 'rejected_type':
      case 'rejected_size':
      case 'failed':
        return 'text-danger';
      default:
        return 'text-body-secondary';
    }
  }

  trackUpload = (_: number, upload: SalesToolkitUpload) => upload.id;
}
